export const COLLABORATION_METHOD_OPTION_KEY = 'CollaborationMethodOption';
export const COLLABORATION_METHOD_SYNC = 'Sync';
export const COLLABORATION_METHOD_ASYNC = 'Async';
export const DESIGN_LIVE_REVIEW_METHOD = 'DesignLive';

export interface ProductOptionWithKey {
    key: { key: string };
}

export interface KeyValueOption {
    key: string;
    value: string;
}

type OptionInput =
    | ReadonlyArray<ProductOptionWithKey | KeyValueOption | null>
    | Record<string, string>
    | null
    | undefined;

/**
 * Checks whether CollaborationMethodOption is present.
 *
 * Accepts either:
 * - A product options array (`[{ key: { key: string } }]`) from a ProductOptionsQuery
 * - A key-value options array (`[{ key: string, value: string }]`) from serviceProductOptions
 * - A selected options record (`Record<string, string>`) built from preselected values
 */
export function hasCollaborationMethodOption(options: OptionInput): boolean {
    if (!options) return false;
    if (!Array.isArray(options)) return COLLABORATION_METHOD_OPTION_KEY in options;

    return options.some(
        (option) => option != null && extractKey(option) === COLLABORATION_METHOD_OPTION_KEY
    );
}

function extractKey(option: ProductOptionWithKey | KeyValueOption): string {
    return 'value' in option ? option.key : option.key.key;
}

/**
 * Merges new product options with existing service product options.
 *
 * - CollaborationMethodOption is set to Sync (DesignLive) or Async based on `withDesignLive`.
 * - Options that already have a value keep their existing value.
 * - New options use the preselected value if available.
 */
export function mergeServiceProductOptions(
    newProductOptions: ReadonlyArray<{
        key: { key: string };
        values: ReadonlyArray<{ key: string; isPreselected: boolean }>;
    }>,
    existingOptions: ReadonlyArray<{ key: string; value: string }> | null | undefined,
    withDesignLive?: boolean
): Array<{ key: string; value: string }> {
    const existingMap = new Map((existingOptions ?? []).map(({ key, value }) => [key, value]));

    return newProductOptions.reduce<Array<{ key: string; value: string }>>((acc, option) => {
        const optionKey = option.key.key;
        const existingValue = existingMap.get(optionKey);

        if (optionKey === COLLABORATION_METHOD_OPTION_KEY) {
            acc.push({
                key: optionKey,
                value: withDesignLive ? COLLABORATION_METHOD_SYNC : COLLABORATION_METHOD_ASYNC,
            });
        } else if (existingValue !== undefined) {
            acc.push({ key: optionKey, value: existingValue });
        } else {
            const preselected = option.values.find((v) => v.isPreselected);
            if (preselected) {
                acc.push({ key: optionKey, value: preselected.key });
            }
        }

        return acc;
    }, []);
}
